import { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Edit } from '../../icons';
import { SearchInput } from '../../components/shared';
import { APPR_HOME_PATH, apprMenuFor, filterApprMenu } from '../../lib/appr-menu';
// 2열 묶음 배치 · 지금 위치를 화면 안에 (2026-10-17 · web 레인 W5 — lib/menu-layout.ts 머리말)
import { APPR_LAYOUT, regroup } from '../../lib/menu-layout';
import { useActiveIntoView } from '../../hooks/useActiveIntoView';
import { useIsAdminViewer } from '../../lib/menu-catalog';
import { listDocuments } from '../../api/approval';
import { fetchMySignature } from '../../api/approval-signature';
import { useApprovalStore } from '../../store/approvalStore';
import { useProfileUiStore } from '../../store/profileUiStore';
import { ApprovalFormPicker } from './ApprovalFormPicker';
import './ApprShell.css';

/**
 * 전자결재 셸 — 좌 240px 트리 + 우 화면(`<Outlet/>`).
 *
 * ── 무엇이 바뀌나 (2026-08-31 · 사용자 지적 「메뉴 양식이 다 틀리다」) ────────
 * 결재만 **양식이 달랐다.** 인사·근태는 셸이 트리를 이고 자식 라우트가 화면을 갈아 끼우는데,
 * 결재는 `ApprovalPage` 한 화면이 **제 안에 220px 사이드바를 들고** 상태로만 움직였다.
 * 그래서 주소가 `/approval` 하나뿐이라 **어느 함도 북마크할 수 없었고**, 트리에 머리(제목·검색)가
 * 없어 옆 메뉴들과 생김새가 달랐다. 이 셸이 그 그릇이다 — 치수·규칙은 근태 셸(`AttShell`)과 같다.
 *
 * ── 왜 가드를 두지 않나 ──────────────────────────────────────────────────
 * 근태·인사 셸과 같은 판단이다. **전 직원이 결재에 들어온다**(기안하고 참조를 받는다).
 * 담당자 화면은 저마다 **서버가** 403 으로 막고, 트리는 관리자에게만 그 묶음을 세운다 —
 * 프론트 가드는 UX 게이트이고 권위 있는 차단은 서버가 한다.
 *
 * ── 「새 결재 올리기」가 트리 머리에 있는 이유 ────────────────────────────
 * 시안 F1·F2·F3 이 트리 머리에 그 단추를 그렸다. 종전에는 **화면 머리마다** 같은 단추가
 * 붙어 있어서, 관리 화면(양식·서식 등)에서도 「기안」 단추가 떴다 — 그 자리에서 할 일이 아니다.
 * 셸이 들고 있으면 **어느 화면에서나 한 자리**이고, 관리 화면의 머리는 제 일(내려받기·만들기)만 한다.
 *
 * ★★ 2026-08-31 — 그 단추가 여는 것이 **창에서 자리로** 바뀌었다(`/approval/new`).
 *    셸은 이제 창을 그리지 않는다 — 기안은 트리를 인 채로 제 화면에서 한다.
 *
 * ★★★ 2026-09-07 — 이름이 **「새 결재」**로 줄고, 누르면 **「결재양식」 창**이 뜬다(사용자 지정).
 *    ⚠ 2026-08-31 의 위 문장과 어긋나 보이지만 **뒤집은 것이 아니다** — 창이 맡는 것은
 *      «양식 고르기 + 딸린 칸»뿐이고, **기안은 그대로 자리**(`/approval/new`)에서 한다.
 *      정본이 「필드 20개를 넘으면 모달이 아니라 화면」이라 적어 둔 그 규율은 그대로다.
 *    ★ 주소(`/approval/new`)는 살아 있다 — 직접 들어오면 그 화면의 첫 걸음이 양식을 고른다.
 */
export function ApprShell() {
  const { pathname } = useLocation();
  const isAdmin = useIsAdminViewer();
  const [query, setQuery] = useState('');
  /**
   * 트리 배지 — **열지 않아도 내 차례가 몇인지 보인다**(근태 「이상 근태」와 같은 규칙).
   * ★ 못 받으면 **숫자를 안 그린다** — 0 으로 그리면 「할 일이 없다」는 거짓말이 된다.
   */
  const [pending, setPending] = useState<number | null>(null);
  const bump = useApprovalStore((s) => s.lastApprovalStatusBump);
  /**
   * 서명을 아직 안 만들었나 (시안 F1 의 「미등록」 표) — **결재를 막지는 않는다.**
   * 안 만들면 결재란에 이름과 날짜만 남는데, 그 사실을 **누르기 전에** 알려 주려는 것이다.
   * ★ 못 읽으면 아무 표도 안 붙인다(옛 서버는 이 라우트를 모른다 — 404).
   */
  const [noSign, setNoSign] = useState(false);
  /** 서명 창이 닫히면 다시 읽는다 — 방금 만든 도장이 트리에 그대로 「미등록」으로 남으면 안 된다. */
  const signatureOpen = useProfileUiStore((s) => s.signatureOpen);
  /** 「결재양식」 창 (2026-09-07) — 트리 머리의 단추가 연다. */
  const [pickerOpen, setPickerOpen] = useState(false);

  const menu = useMemo(() => regroup(apprMenuFor(isAdmin), APPR_LAYOUT), [isAdmin]);
  const groups = useMemo(() => filterApprMenu(menu, query), [menu, query]);
  useActiveIntoView('.aprnav', pathname);
  const hit = groups.some((g) => g.items.length > 0);

  useEffect(() => {
    let alive = true;
    void listDocuments('incoming', { limit: 50 })
      .then((r) => { if (alive) setPending(r.items.filter((d) => d.status === 'IN_PROGRESS').length); })
      .catch(() => { if (alive) setPending(null); });
    return () => { alive = false; };
    // SSE 로 결재 상태가 바뀌면 배지도 함께 따라간다.
  }, [bump]);

  useEffect(() => {
    if (signatureOpen) return;
    let alive = true;
    void fetchMySignature()
      .then((sig) => { if (alive) setNoSign(!sig.hasImage); })
      .catch(() => { if (alive) setNoSign(false); });
    return () => { alive = false; };
  }, [signatureOpen]);



  /** 「결재 홈」(뿌리)만 정확히 일치로 켠다 — 아니면 하위 화면에서 둘이 함께 켜진다. */
  const isOn = (to: string) =>
    to === APPR_HOME_PATH ? pathname === APPR_HOME_PATH : pathname === to || pathname.startsWith(`${to}/`);

  return (
    <div className="aprshell">
      <nav className="aprnav" aria-label="전자결재 메뉴">
        <div className="aprnav__head">
          <h2 className="aprnav__title">전자결재</h2>
          {/* 채택 3단계 ③ 결재 — 손으로 짠 검색칸을 정본 `SearchInput` 으로(돋보기 · 지우기 · Esc). */}
          <SearchInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClear={() => setQuery('')}
            placeholder="결재 메뉴 검색"
            aria-label="결재 메뉴 검색"
            fullWidth
          />
          {/*
            시안 F1 — 트리 머리의 단추. 어느 화면에 있든 여기 하나뿐이다.
            ★ 「올리기」를 뗐다 — 창 아래의 「기안하기」가 이미 그 말을 한다(정본 「말」 절:
              동사는 한 번만).
          */}
          <button type="button" className="aprnav__cta" onClick={() => setPickerOpen(true)}>
            <Edit size={14} aria-hidden />새 결재
          </button>
        </div>

        {groups.map((g) => (
          <div className="aprnav__group" key={g.label}>
            <div className="aprnav__glabel">{g.label}</div>
            {g.items.map((i) => {
              const Icon = i.icon;
              /**
               * ★ 서명 · 도장만 **창**이다 — 프로필의 「내 서명」과 같은 창을 연다.
               *   주소를 따로 내면 같은 화면이 두 벌이 되고, 언젠가 한쪽만 고쳐진다.
               */
              if (i.action === 'signature') {
                return (
                  <button
                    key={i.label}
                    type="button"
                    className="aprnav__item"
                    onClick={() => useProfileUiStore.getState().openSignature()}
                  >
                    <Icon size={17} aria-hidden />
                    <span className="aprnav__nm">
                      {i.label}
                      {noSign && <span className="aprnav__tag">미등록</span>}
                    </span>
                    <span className="aprnav__hint">{i.hint}</span>
                  </button>
                );
              }
              const to = i.to!;
              return (
                <Link
                  key={to}
                  to={to}
                  className={`aprnav__item${isOn(to) ? ' is-on' : ''}`}
                  aria-current={isOn(to) ? 'page' : undefined}
                >
                  <Icon size={17} aria-hidden />
                  <span className="aprnav__nm">
                    {i.label}
                    {/* 내 차례 배지 — 「결재 대기」 가 결재함 마디로 들어가며(L2) 「결재함」 에 붙는다 */}
                    {(to === '/approval/box' || to === '/approval/pending') && pending !== null && pending > 0 && (
                      <span className="aprnav__badge" aria-label={`${pending}건`}>{pending}</span>
                    )}
                  </span>
                  <span className="aprnav__hint">{i.hint}</span>
                </Link>
              );
            })}
          </div>
        ))}
        {!hit && <p className="aprnav__none">찾는 메뉴가 없습니다.</p>}
      </nav>

      <div className="aprnav__body">
        <Outlet />
      </div>

      {/* 「결재양식」 — 고르는 자리는 창, 쓰는 자리는 화면(정본 F1 `picker`). */}
      <ApprovalFormPicker open={pickerOpen} onClose={() => setPickerOpen(false)} />
    </div>
  );
}
